// CNS API models generated from cns.yaml
import type { components } from "@/models/generated/cns-api";
import type {
  CNSDomainEvent,
  CNSExperimentAssignedEvent,
  CNSExperimentStatusChangedEvent,
  CNSExperimentDueSoonEvent,
  CNSExperimentOverdueEvent,
  CNSShareInviteAcceptedEvent,
  CNSWorkspaceMemberAddedEvent,
  CNSWorkspaceMemberRemovedEvent,
  CNSInsightUpdatedEvent,
  CNSConversationSuggestionsChangedEvent,
} from "@/models/generated/asyncapi-events";
export type {
  CNSDomainEvent,
  CNSExperimentAssignedEvent,
  CNSExperimentStatusChangedEvent,
  CNSExperimentDueSoonEvent,
  CNSExperimentOverdueEvent,
  CNSShareInviteAcceptedEvent,
  CNSWorkspaceMemberAddedEvent,
  CNSWorkspaceMemberRemovedEvent,
  CNSInsightUpdatedEvent,
  CNSConversationSuggestionsChangedEvent,
} from "@/models/generated/asyncapi-events";

export type {
  CNSConversationSuggestions,
  CNSConversationSuggestion,
  CNSSuggestionPresentation,
  CNSSuggestionAction,
  CNSSuggestionActivationRequest,
  CNSSuggestionActivation,
  CNSSuggestionActivationOutcome,
  CNSSuggestionsReplacePayload,
} from "@/models/suggestion";

export interface CNSUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  enabled: boolean;
  /** Whether the user can access the admin portal (from GET /auth/v1/user/current). */
  admin?: boolean;
}

/** Workspace analytics (from GET /cns/v1/workspaces/{id}/analytics). */
export interface CNSWorkspaceAnalytics {
  projects: number;
  chats: number;
  experiments: number;
  learnings: number;
  insights: number;
  documents: number;
  medianLearningCycleDays: number | null;
  learningCycleSampleSize: number;
}

export type CNSWorkspaceType = 'PERSONAL' | 'SHARED' | 'ENTERPRISE';

/** All workspace type values (for validation/UI). */
export const WORKSPACE_TYPES: readonly CNSWorkspaceType[] = ['PERSONAL', 'SHARED', 'ENTERPRISE'];

export interface CNSWorkspace {
  id: string; // wks_ prefix (public ID)
  name: string;
  type: CNSWorkspaceType;
  createdAt: string; // ISO-8601 UTC string
  updatedAt?: string; // ISO-8601 UTC string, optional
  /** true when the user has access as member via invite, not as owner */
  isShared?: boolean;
}

export interface CNSCreateWorkspace {
  name: string;
  type?: CNSWorkspaceType;
}

/**
 * Opaque plan tier code (e.g. "EXPLORER", "BUILDER", "SCALE"). A display/grouping label only;
 * ordering derives from {@link CNSPlanTemplateSummary.sortOrder} and behavior from capability flags,
 * not from this value. New tiers are added in the database without a code change.
 */
export type CNSPlanTier = string;
export type CNSBillingPeriodCycle = "MONTHLY" | "YEARLY";
export type CNSCreditEntryType =
  | "PLAN_RESET"
  | "TOPUP_PURCHASED"
  | "CONSUMPTION"
  | "MANUAL_GRANT"
  | "MANUAL_ADJUSTMENT";
export type CNSCreditBucketType = "PLAN" | "TOPUP";

export interface CNSPlanTemplateSummary {
  id: string;
  name: string;
  tier: CNSPlanTier;
  billingPeriodCycle: CNSBillingPeriodCycle;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  monthlyPlanCredits: number;
  recommended: boolean;
  featureHighlights: string;
  defaultPlan: boolean;
  unlimitedPlan: boolean;
  /** Authoritative low-to-high plan ordering; used to derive upgrade vs downgrade. */
  sortOrder: number;
}

export interface CNSWorkspaceBillingProfileAssignment {
  workspacePublicId: string;
  planTemplatePublicId: string;
  planTier: CNSPlanTier;
  billingPeriodCycle: CNSBillingPeriodCycle;
  monthlyPlanCredits: number;
  defaultPlan: boolean;
  unlimitedPlan: boolean;
}

/**
 * Top-up SKU surfaced by `GET /credit-packs` and rendered in the {@code TopUpDialog}.
 * Prices are integer cents in `currency`. The backend filters out archived / inactive packs.
 */
export interface CNSCreditPackSummary {
  id: string;
  code: string;
  name: string;
  creditAmount: number;
  priceCents: number;
  currency: string;
  recommended: boolean;
}

/**
 * Returned by `POST /workspaces/{id}/billing/{checkout-session,topup-checkout-session}`.
 * Browser is expected to perform a full redirect to {@link sessionUrl}; Stripe lands the user
 * back on `/dashboard/billing/{success,cancel}`.
 */
export interface CNSBillingCheckoutSession {
  sessionId: string;
  sessionUrl: string;
  expiresAt: string;
}

/** Returned by `POST /workspaces/{id}/billing/customer-portal-session`. Redirect-only. */
export interface CNSBillingPortalSession {
  portalUrl: string;
}

/**
 * Subscription lifecycle states emitted by the backend for the workspace billing summary.
 * `NONE` covers free Explorer workspaces with no `payment_provider_subscription` row.
 */
export type CNSSubscriptionStatus =
  | "NONE"
  | "INCOMPLETE"
  | "INCOMPLETE_EXPIRED"
  | "ACTIVE"
  | "TRIALING"
  | "PAST_DUE"
  | "CANCELED"
  | "UNPAID"
  | "PAUSED";

/**
 * Composite read returned by `GET /workspaces/{id}/billing/subscription`. Combines domain billing
 * profile (plan, cycle, currency, period boundaries) with the active payment-provider subscription
 * row (status, cancel-at-period-end, current-period-end). Free workspaces emit `subscriptionStatus`
 * `NONE` and omit subscription-only fields.
 */
export interface CNSWorkspaceBillingSubscription {
  workspacePublicId: string;
  planTemplatePublicId: string;
  billingPeriodCycle: CNSBillingPeriodCycle;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  currency: string;
  subscriptionStatus: CNSSubscriptionStatus;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string;
  paymentProviderCode?: string;
}

/**
 * Fulfilment status of a confirmed checkout, independent of the provider's raw payment status.
 * `COMPLETE` = payment captured and local state fulfilled; `PENDING` = captured but local fulfilment
 * still settling (e.g. top-up credits land via webhook); `OPEN` = not yet paid; `EXPIRED` = session
 * expired before completion.
 */
export type CNSBillingCheckoutConfirmationStatus =
  | "COMPLETE"
  | "PENDING"
  | "OPEN"
  | "EXPIRED";

/** Checkout session mode, mirroring Stripe's subscription / payment modes. */
export type CNSBillingCheckoutMode = "SUBSCRIPTION" | "PAYMENT";

/**
 * Returned by `POST /workspaces/{id}/billing/checkout-session/{checkoutSessionId}/confirm`. Carries the
 * authoritative fulfilment status read straight from Stripe plus the post-confirmation billing summary,
 * so the success page can resolve the purchase in a single call instead of polling.
 */
export interface CNSBillingCheckoutConfirmation {
  status: CNSBillingCheckoutConfirmationStatus;
  mode: CNSBillingCheckoutMode;
  checkoutSessionId: string;
  subscription: CNSWorkspaceBillingSubscription;
}

export interface CNSProject {
  id: string;
  title: string;
  createdAt: string; // ISO-8601 UTC string
  updatedAt?: string; // ISO-8601 UTC string, optional
  workspaceId?: string;
  groupId?: string;
}

/** All-time analytics for a project (GET /cns/v1/projects/{id}/analytics). */
export interface CNSProjectAnalytics {
  chatsStarted: number;
  selectedAssumptions: number;
  selectedExperiments: number;
  learningsSynthesized: number;
  insightsGenerated: number;
  documentsAdded: number;
  /** Median whole calendar days (browser IANA zone sent with the request), assumption to learning; null if no qualifying cycles. */
  medianLearningCycleDays: number | null;
  learningCycleSampleSize: number;
}

/** Discriminator for which workflow transition a next-step row represents (matches OpenAPI ProjectNextStepKind). */
export type CNSProjectNextStepKind =
  | "HYPOTHESIS_FINALIZE"
  | "HYPOTHESIS_NEEDS_EXPERIMENT"
  | "EXPERIMENT_REVIEW"
  | "EXPERIMENT_ASSIGN"
  | "EXPERIMENT_CAPTURE_RESULTS"
  | "EXPERIMENT_LOCK_TRACKER"
  | "LEARNING_VALIDATE"
  | "CONVERSATION_PENDING_ACTION";

/** Visual urgency for a next-step row. Drives sort order and badge colour. */
export type CNSProjectNextStepSeverity = "INFO" | "ATTENTION" | "OVERDUE";

/** Entity type a next-step row references. Used to build the deep-link path to the detail page. */
export type CNSProjectNextStepEntityType = "hypothesis" | "experiment" | "learning" | "insight" | "conversation";

/**
 * One open item for a project, state-derived by the backend (GET /cns/v1/projects/{id}/next-steps).
 * Items are computed on read; nothing is persisted.
 */
export interface CNSProjectNextStep {
  /** Deterministic identifier (e.g. "experiment:exp_abc:approve"); stable across calls and safe as a React key. */
  stepId: string;
  kind: CNSProjectNextStepKind;
  severity: CNSProjectNextStepSeverity;
  projectPublicId: string;
  entityType: CNSProjectNextStepEntityType;
  entityPublicId: string;
  /** Human-readable identifier (truncated) for the row body. */
  entityTitle: string;
  /** Verb-led action label rendered on the row (e.g. "Review draft experiment"). */
  primaryActionLabel: string;
  /** Optional trailing path segment appended to the entity detail URL. */
  deepLinkSuffix?: string | null;
  /**
   * Owning conversation public id when lineage resolves (cnv_*).
   * Prefer workbench deep links when present; null means project conversations list fallback.
   */
  conversationPublicId?: string | null;
  /** Public ID of the user this step is assigned to, when applicable (currently only experiments). */
  assigneeUserPublicId?: string | null;
  /** Whole calendar days since the underlying entity entered the current state. */
  ageDays: number;
  /** Stable Action Request type when this row corresponds to a Workbench action. */
  actionType?: string | null;
}

/** Response payload for GET /cns/v1/projects/{id}/next-steps. */
export interface CNSProjectNextStepsView {
  /** Ordered next-step rows (OVERDUE first, then ATTENTION, then INFO; newest first within each tier). Capped at 50 server-side. */
  items: CNSProjectNextStep[];
  /** Total number of open items before the server-side cap is applied. */
  totalCount: number;
}

/** Filter passed as the `scope` query parameter; "all" returns everything, "mine" filters to the current user. */
export type CNSProjectNextStepsScope = "mine" | "all";

/** Response for GET /cns/v1/projects/{id}/conversation-resolution. */
export interface CNSConversationResolution {
  /** Owning conversation public id when lineage resolves; null when orphaned or unknown. */
  conversationPublicId: string | null;
}

export interface CNSCreateProject {
  title: string;
  /** Workspace public ID (e.g. wks_xxx). Required when creating a project. */
  workspaceId: string;
}

export interface CNSConversation {
  id: string;
  title: string;
  createdAt: string; // ISO-8601 UTC string
  updatedAt?: string; // ISO-8601 UTC string, optional
  projectId: string;
  parentConversationPublicId?: string | null;
  /** Computed Workbench workflow stage for the Hub conversation index. */
  currentStage?: string | null;
  /** Short label for the primary artifact chip (experiment or learning title). */
  primaryArtifactLabel?: string | null;
  /** Status tone for the primary artifact chip. */
  primaryArtifactStatus?: string | null;
  /** Short attention line when this conversation needs human action. */
  attentionHint?: string | null;
  /** One-line selected-agent label for the latest routing action, or null when none. */
  lastAgentActionLine?: string | null;
  /** Effective server-authorized access for the authenticated actor. */
  access: CNSConversationAccess;
}

/**
 * Effective OpenFGA-backed access projected with every Conversation response.
 * These booleans are authority; raw share records are provenance/list UI only.
 */
export interface CNSConversationAccess {
  canEditConversation: boolean;
  canViewProject: boolean;
  canEditProject: boolean;
}

export type CNSActionContinuationMode =
  components["schemas"]["ActionSubmissionResponse"]["continuationMode"];
export type CNSActionReceipt = components["schemas"]["ActionReceipt"];
export type CNSActionSubmissionResponse =
  components["schemas"]["ActionSubmissionResponse"];
export type CNSActionContinuationReservationResponse =
  components["schemas"]["ActionContinuationReservationResponse"];

export interface CNSCreateConversation {
  title: string;
  projectId: string;
}

/** Entity type for share (POST /share) and for current-user share items. */
export type CNSShareEntityType = "project" | "conversation" | "workspace";

/** Role in a share (no owner; owner = not in shares list). */
export type CNSCurrentUserShareRole = "editor" | "viewer" | "member";

/** Share entity from GET /shares/users/current-user. Used to derive isShared and role per entity. */
export interface CNSShare {
  entityType: CNSShareEntityType;
  entityPublicId: string;
  role: CNSCurrentUserShareRole;
  /** Share public ID (e.g. shr_xxx); use for DELETE /share/{sharePublicId}. */
  id: string;
  createdAt: string; // ISO-8601 UTC string
}

/** Role to grant when sharing. For workspace optional (default member). */
export type CNSShareRole = "viewer" | "editor" | "member";

/** Request body for POST /share. Target user is specified by email only. */
export interface CNSShareRequest {
  entityType: CNSShareEntityType;
  entityPublicId: string;
  email: string;
  role?: CNSShareRole;
}

/** Owner in share list (workspace, project, or conversation). Name and email only. */
export interface CNSShareListOwner {
  name?: string;
  email?: string;
}

/** Member in share list. Share id for remove, name, email, role. */
export interface CNSShareListMember {
  id: string; // share public ID (shr_xxx)
  name?: string;
  email?: string;
  role: string;
  createdAt?: string;
}

/** Response of GET /share?entityType=&entityPublicId= */
export interface CNSShareListResponse {
  owner?: CNSShareListOwner;
  members: CNSShareListMember[];
}

export interface CNSChatMemorySummary {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  createdAt: string; // ISO-8601 UTC string
}

export interface CNSMessage {
  id: string; // msg_ prefix
  parentId?: string | null; // Parent message ID for threading
  role: 'USER' | 'ASSISTANT' | 'TOOL' | 'SYSTEM';
  status?: string;
  error?: string;
  rawMessage: any; // Full Assistant UI message structure
  createdAt: string;
}

export interface CNSEntityHolder {
  id: string;
  conversationId: number;
  assistantMessagePublicId?: string | null;
  streamPartId?: string | null;
  entityType: string;
  entityData: any;
  toolInvoke?: {
    toolCallId: string;
    requiresAction: boolean;
    actionType?: string;
    toolResult?: any;
    reason?: string;
    /** Native Action Request contract marker. Absent on historical holders. */
    materializationContract?: string;
    /** Durable workflow surface lane. Absent on historical assistant-turn holders. */
    actionRequestSlot?: string;
  };
  createdAt: string;
  updatedAt: string;
  createdByAgent: string;
}

// Generic pagination response (Spring style)
export interface CNSPaginationResponse<T> {
  totalElements: number;
  totalPages: number;
  sort?: CNSSort;
  first: boolean;
  last: boolean;
  number: number;
  pageable?: CNSPageable;
  numberOfElements: number;
  size: number;
  empty: boolean;
  content: T[];
}

export interface CNSSort {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
}

export interface CNSPageable {
  page: number;
  size: number;
  sort: string[];
}

// Specific paginated types
export type CNSProjectPage = CNSPaginationResponse<CNSProject>;
export type CNSConversationPage = CNSPaginationResponse<CNSConversation>;
export type CNSChatMemorySummaryPage = CNSPaginationResponse<CNSChatMemorySummary>;
export type CNSMessagePage = CNSPaginationResponse<CNSMessage>;
export type CNSEntityHolderPage = CNSPaginationResponse<CNSEntityHolder>;
export type CNSInnovationActionPage = CNSPaginationResponse<CNSInnovationAction>;
export type CNSWorkspaceCreditLedgerEntryPage = CNSPaginationResponse<CNSWorkspaceCreditLedgerEntry>;

export interface CNSWorkspaceCreditBalance {
  workspaceId: string;
  planCreditsRemaining: number;
  topupCreditsRemaining: number;
  totalCreditsRemaining: number;
  planCreditsUsed: number;
  unlimited: boolean;
  planTier?: CNSPlanTier;
  monthlyPlanCredits?: number;
  billingPeriodEnd?: string;
}

export interface CNSInnovationAction {
  id: string;
  workspacePublicId?: string;
  projectPublicId?: string;
  userPublicId?: string;
  actionType?: string;
  status?: string;
  creditsUsed?: number;
  idempotencyKey?: string;
  sourceKind?: string;
  sourceRef?: string;
  createdAt?: string;
}

export interface CNSWorkspaceCreditLedgerEntry {
  id: string;
  workspacePublicId?: string;
  accountPublicId?: string;
  innovationActionPublicId?: string;
  entryType?: CNSCreditEntryType;
  bucketType?: CNSCreditBucketType;
  amountDelta?: number;
  reason?: string;
  createdAt?: string;
}

export interface CNSAdminWorkspaceCreditSummary {
  workspaceId: string;
  workspaceName: string;
  ownerFullName?: string;
  ownerEmail: string;
  planTier?: CNSPlanTier;
  planCreditsRemaining?: number;
  topupCreditsRemaining?: number;
  totalCreditsRemaining: number;
  unlimited: boolean;
  billingPeriodEnd?: string;
  updatedAt?: string;
}

export type CNSAdminWorkspaceCreditSummaryPage = CNSPaginationResponse<CNSAdminWorkspaceCreditSummary>;

// Login request
export interface CNSLoginRequest {
  username: string;
  password: string;
}

// Experiment Management System (EMS) Models

export type ExperimentStatus = 'DRAFT' | 'APPROVED' | 'RUNNING' | 'COMPLETED' | 'ARCHIVED';
export type ExperimentTheme = 'desirability' | 'feasibility' | 'viability';
export type HypothesisTheme = ExperimentTheme;

/** Hypothesis lifecycle (API wire format: uppercase). */
export type HypothesisStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

/** Lineage edge input for create APIs (e.g. POST /hypotheses, POST /experiments). */
export interface CNSLineageEdgeInput {
  targetType: string;
  targetPublicId: string;
}

/** Request body for POST /hypotheses. */
export interface CNSCreateHypothesis {
  id: string; // frontend-provided hyp_ KSUID
  projectId: string;
  statement: string;
  theme: HypothesisTheme;
  /** Public IDs of the assumptions this hypothesis was derived from (e.g. asm_ KSUID). */
  sourceAssumptionIds?: string[];
  lineageEdges?: CNSLineageEdgeInput[];
  /** Tool call ID of the experiment_design entity collection; used to link this hypothesis to an entity holder for preview links. */
  toolCallId?: string;
}

/** Response from POST /hypotheses (minimal for create flow). */
export interface CNSHypothesis {
  id: string;
  projectId?: string;
  statement?: string;
  theme?: HypothesisTheme;
  status?: HypothesisStatus;
  sourceAssumptionIds?: string[];
  createdAt?: string;
  updatedAt?: string;
}

/** Request body for POST /assumptions. */
export interface CNSCreateAssumption {
  id: string; // frontend-provided asm_ KSUID
  projectId: string;
  statement: string;
  evidence: number; // 1-5
  importance: number; // 1-5
  lineageEdges?: CNSLineageEdgeInput[];
  toolCallId?: string;
}

/** Response from POST /assumptions. */
export interface CNSAssumption {
  id: string;
  projectId?: string;
  statement?: string;
  evidence?: number;
  importance?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** Matches backend LearningAnnotation / Insight Java enums (REST wire format). */
export type LearningStatus = "DRAFT" | "VALIDATED";
export type InsightStatus = "DRAFT" | "VALIDATED";

export interface CNSKeyMetric {
  id: string;
  name: string;
  target: string;
  notes?: string | null;
}

export interface CNSOperationalDetails {
  setupComplexity: number; // 1-5
  runTime: number; // 1-5
  cost: number; // 1-5
  dataReliability: number; // 1-5
}

export interface CNSExperiment {
  id: string;
  // projectId and userId removed - use navigation context (useNavigationData) instead
  title: string;
  theme: ExperimentTheme;
  statement: string;
  description: string;
  experimentType: string;
  successCriteria: string;
  // keyMetrics comes as JsonNode from backend but is typed as array for frontend usage
  keyMetrics: CNSKeyMetric[];
  operationalDetails: CNSOperationalDetails;
  status: ExperimentStatus;
  assigneeUserId?: string | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  assignedByUserId?: string | null;
  assignedByName?: string | null;
  assignedByEmail?: string | null;
  assignedAt?: string | null;
  /** Optional ISO 8601 timestamp for when the experiment is due to complete. */
  dueAt?: string | null;
  notes?: CNSExperimentNote[] | null;
  timeline?: string | null;
  /**
   * @deprecated Superseded by the in-app Data Tracker
   * (`CNSDataTracker` + `CNSDataTrackerRow`, see below). The
   * `Download Activity Data Template` quick action and the
   * downstream `.txt` plumbing have been removed; this field is
   * retained on the model for one release of back-compat with
   * experiments approved before the cutover and will be removed
   * after that grace period. Do not consume this field in new UI;
   * render the tracker tab instead.
   */
  activityDataTemplate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CNSExperimentNote {
  id: string;
  author: string;
  content: string;
  timestamp: string;
}

/** Request body for POST /experiments (manual experiment creation). */
export interface CNSCreateExperiment {
  id: string; // frontend-provided exp_ KSUID
  projectId: string;
  title: string;
  theme: ExperimentTheme;
  statement: string;
  experimentType: string;
  description: string;
  successCriteria: string;
  assigneeUserId?: string | null;
  /** Optional target completion timestamp (ISO 8601). */
  dueAt?: string | null;
  keyMetrics?: CNSKeyMetric[];
  operationalDetails?: CNSOperationalDetails;
  /** When provided, experiment is linked to this existing hypothesis (public ID). */
  hypothesisId?: string;
  /** Optional lineage edges (e.g. conversation, entity_holder) for association. */
  lineageEdges?: CNSLineageEdgeInput[];
  /** Tool call ID of the experiment_design entity collection; used to link this experiment to an entity holder for preview links. */
  toolCallId?: string;
}

// CNSUpdateExperiment removed - use PATCH with JSON Patch operations instead
// Example: [{ op: "replace", path: "/status", value: "APPROVED" }]

// JSON Patch operation type
export interface JsonPatchOperation {
  op: "replace" | "add" | "remove" | "test" | "move" | "copy";
  path: string;
  value?: any;
  from?: string;
}

// Entity Change History
export interface CNSEntityChangeHistory {
  id: string;
  publicId: string;
  entityType: string;
  entityPublicId: string;
  patchOperations: JsonPatchOperation[];
  createdAt: string; // ISO-8601 UTC string
}

export type CNSEntityChangeHistoryPage = CNSPaginationResponse<CNSEntityChangeHistory>;

export interface CNSObservation {
  category: string;
  content: string;
  confidence: number; // Evidence-confidence rank: 1 high, 5 low
}

export interface CNSRecommendedAction {
  id: string; // KSUID
  action: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  effort: 'LOW' | 'MEDIUM' | 'HIGH';
  impact: 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface CNSLearningAnnotation {
  id: string; // KSUID
  summary?: string;
  tags?: string[];
  notes?: string;
}

export interface CNSLearning {
  id: string;
  experimentId: string;
  title: string;
  /** Workflow status (API: DRAFT | VALIDATED). */
  status: LearningStatus;
  observations: CNSObservation[];
  dataReliability: number;
  insights: CNSInsight[];
  recommendedActions: CNSRecommendedAction[];
  annotation?: CNSLearningAnnotation;
  createdAt: string;
  updatedAt?: string;
}

// CNSUpdateLearning removed - use PATCH with JSON Patch operations instead
// Example workflow status: [{ op: "replace", path: "/status", value: "VALIDATED" }]

export interface CNSInsight {
  id: string; // KSUID
  insight: string; // Required, from agent
  implications?: string; // User-provided
  confidence?: number; // User-provided evidence-confidence rank: 1 high, 5 low
  tags: string[]; // User-provided
  status: InsightStatus; // DRAFT | VALIDATED (API)
  learningPublicId?: string | null;
  experimentPublicId?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Paginated types
export type CNSExperimentPage = CNSPaginationResponse<CNSExperiment>;
export type CNSHypothesisPage = CNSPaginationResponse<CNSHypothesis>;
export type CNSAssumptionPage = CNSPaginationResponse<CNSAssumption>;
export type CNSLearningPage = CNSPaginationResponse<CNSLearning>;
export type CNSInsightPage = CNSPaginationResponse<CNSInsight>;

// Data Tracker — in-app data collection grid backing the LOCKED snapshot
// document used by native Learning Synthesis.

export type CNSDataTrackerColumnType =
  components["schemas"]["DataTrackerColumnType"];

export const DATA_TRACKER_COLUMN_TYPES = [
  'text',
  'longText',
  'number',
  'date',
  'select',
  'multiSelect',
  'url',
  'attachment',
] as const satisfies readonly CNSDataTrackerColumnType[];

export type CNSDataTrackerColumn = components["schemas"]["DataTrackerColumn"];

export type CNSDataTrackerSource = 'AGENT_GENERATED' | 'USER_EDITED';
export type CNSDataTrackerStatus = 'DRAFT' | 'ACTIVE' | 'LOCKING' | 'LOCKED';

export interface CNSDataTracker {
  id: string; // dtk_ KSUID
  experimentId: string;
  projectId: string;
  columns: CNSDataTrackerColumn[];
  source: CNSDataTrackerSource;
  status: CNSDataTrackerStatus;
  /** Public ID of the materialized Markdown snapshot Document; populated when LOCKED. */
  snapshotDocumentId: string | null;
  /** Filename of the snapshot Document, surfaced as the "view in Documents" chip. */
  snapshotDocumentName?: string | null;
  lockedAt?: string | null;
  lockedByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Cell value union per column type:
 *   text | longText | url        -> string | null
 *   number                       -> number | null
 *   date                         -> string | null  (ISO-8601)
 *   select                       -> string | null
 *   multiSelect                  -> string[]
 *   attachment                   -> string[]       (documentPublicId[])
 *
 * The grid is permissive at the type level so the same row payload can flow through
 * paste/import paths; per-cell renderers narrow on column.type at edit time.
 */
/**
 * Wire format for a data tracker cell. Per-column-type contracts:
 * - `text`, `longText`: plain `string` (or `null` for empty).
 * - `number`: numeric `number` (or `null`).
 * - `date`: `YYYY-MM-DD` string only — never an ISO datetime, never timezone-shifted.
 *   Editors persist a typed `YYYY-MM-DD` string (or `null` when cleared).
 * - `select`: a single option label `string` (must match `column.options`).
 * - `multiSelect`: `string[]` of option labels.
 * - `url`: a `string` URL (validated `http(s)` at render time).
 * - `attachment`: a Document public ID `string`.
 *
 * Loose runtime types (`string | number | string[] | null`) reflect what flows
 * through the paste/import pipelines; per-cell renderers narrow on `column.type`.
 */
export type CNSDataTrackerCellValue = string | number | string[] | null;

/**
 * Draft column schema returned by the data-tracker columns agent. The `columns` field
 * is shaped identically to {@link CNSDataTracker}'s `columns`, so the dialog can hand
 * it straight to {@link CNSDataTracker} create / column-replace endpoints when the
 * user accepts the suggestion.
 */
export interface CNSDataTrackerColumnSuggestion {
  /** Human-readable label for what one row represents (e.g. "Survey respondent"). */
  rowUnit: string;
  /** 1-2 sentences explaining the chosen row grain and column set. */
  rationale: string;
  /** Proposed columns; order is the grid column order. */
  columns: CNSDataTrackerColumn[];
}

export interface CNSDataTrackerRow {
  id: string; // dtr_ KSUID
  trackerId: string;
  /**
   * Lexorank fractional ordering key (sortable as plain text). Server-generated;
   * the server appends new rows at the tail by chaining
   * `LexorankKey.between(last, MAX)` on insert. Clients never compute or patch
   * this field directly.
   */
  rowIndex: string;
  data: Record<string, CNSDataTrackerCellValue>;
  createdByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

// --- Notifications & realtime events ---------------------------------------

// Realtime event payload contracts are generated from AsyncAPI:
// src/models/generated/asyncapi-events.ts

/** Per-recipient notification row (`GET /notifications`, STOMP push payload). */
export interface CNSNotification {
  /** KSUID public ID, e.g. `ntf_xxx`. */
  id: string;
  /** Envelope version for backwards-compatible client parsing. */
  eventVersion?: number;
  recipientUserPublicId?: string;
  domainEventPublicId?: string | null;
  /** Event class slug (matches `event.name` and `name()` on the backend). */
  eventType: string;
  entityType?: string | null;
  entityPublicId?: string | null;
  workspacePublicId?: string | null;
  projectPublicId?: string | null;
  /** Owning workbench conversation when lineage resolves (cnv_*). */
  conversationPublicId?: string | null;
  summary?: string | null;
  /** App-relative path from backend when actionable; null means dismiss-only. Omitted on older APIs. */
  destination?: string | null;
  priority: "LOW" | "MEDIUM" | "HIGH";
  /** Initiator of the underlying domain action, when applicable (AsyncAPI envelope metadata). */
  initiatorUserPublicId?: string | null;
  /** Discriminated union by `event.name`; may be null when payload was not serialisable. */
  event?: CNSDomainEvent | null;
  seenAt?: string | null;
  readAt?: string | null;
  createdAt: string;
}

export interface CNSNotificationPage {
  content: CNSNotification[];
  totalElements: number;
  totalPages: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
}

export interface CNSNotificationUnseenCount {
  count: number;
}

export interface CNSNotificationPreference {
  /** Event class slug, e.g. `experimentAssignedEvent`. */
  name: string;
  inApp: boolean;
  email: boolean;
}

export type CNSWorkflowStage = components["schemas"]["WorkflowStage"];

export type CNSDecisionVerdict = "PERSEVERE" | "PIVOT" | "KILL";

export interface CNSDecision {
  id: string;
  learningPublicId: string;
  verdict: CNSDecisionVerdict;
  rationale?: string;
  confidenceLevel?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Closed concurrency partitions for active Action Requests. */
export type CNSActionRequestSlot =
  components["schemas"]["AssistantTurnActionReference"]["slot"];
export type CNSAssistantTurnActionReference =
  components["schemas"]["AssistantTurnActionReference"];
export type CNSWorkflowActionView =
  components["schemas"]["WorkflowActionView"];
export type CNSCompletedWorkflowActionView =
  components["schemas"]["CompletedWorkflowActionView"];
export type CNSActiveActionRequest =
  components["schemas"]["ActiveActionRequestView"];
export type CNSWorkflowSnapshot = components["schemas"]["WorkflowSnapshot"];
